// ============================================================
// Page Retours Clients — Lot L11 du plan SaaS (frontend)
// ============================================================
// Workflow simplifié : à partir d'une vente, lister les lignes,
// choisir les articles retournés + résolution (remboursement
// espèces / avoir / échange).
// ============================================================

import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { RotateCcw, Plus, X, Loader, Eye, Search } from 'lucide-react';
import InputMontant from '../../components/common/InputMontant';
import { getDeviseCourante } from '../../utils/format';
import toast from 'react-hot-toast';

const TYPES_RES = {
  remboursement_total:    { label: 'Remb. total',    color: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' },
  remboursement_partiel:  { label: 'Remb. partiel',  color: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' },
  avoir:                  { label: 'Avoir',          color: 'bg-yellow-500/15 text-yellow-300 border-yellow-500/30' },
  echange:                { label: 'Échange',        color: 'bg-blue-500/15 text-blue-300 border-blue-500/30' },
};
function fmt(n) { return Number(n || 0).toLocaleString('fr-FR') + ' ' + getDeviseCourante(); }
function fmtDate(iso) { return iso ? new Date(iso).toLocaleDateString('fr-FR') : '—'; }

function ModalCreer({ onClose, onSuccess }) {
  // Mode de retour : depuis une vente existante, ou « sans reçu » (libre)
  const [modeRetour, setModeRetour] = useState('vente'); // 'vente' | 'libre'

  // — Mode « depuis une vente » —
  const [ventes, setVentes] = useState([]);        // ventes récentes (avec lignes)
  const [searchVente, setSearchVente] = useState('');
  const [vente, setVente] = useState(null);        // vente sélectionnée
  const [retours, setRetours] = useState({});      // { variante_id: qte_retournee }

  // — Mode « sans reçu » —
  const [catalogue, setCatalogue] = useState([]);  // variantes (stock) pour la recherche
  const [searchLibre, setSearchLibre] = useState('');
  const [lignesLibres, setLignesLibres] = useState([]); // [{variante_id, produit_nom, modele, prix_unitaire, quantite}]
  const [boutiques, setBoutiques] = useState([]);
  const [boutiqueId, setBoutiqueId] = useState('');

  // — Commun —
  const [type, setType] = useState('remboursement_total');
  const [mode, setMode] = useState('especes');
  const [montantPartiel, setMontantPartiel] = useState('');
  const [motif, setMotif] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  // Charger : ventes des 7 derniers jours (avec leurs lignes), catalogue, boutiques
  useEffect(() => {
    const d = new Date(); d.setDate(d.getDate() - 7);
    const debut = d.toISOString().split('T')[0];
    const fin = new Date().toISOString().split('T')[0];
    api.get('/ventes-magasin', { params: { date_debut: debut, date_fin: fin, limit: 100 } })
      .then(r => setVentes(r.data.ventes || [])).catch(() => {});
    api.get('/stock?limit=500').then(r => setCatalogue(r.data.stock || [])).catch(() => {});
    api.get('/boutiques').then(r => {
      const actives = (r.data || []).filter(b => b.statut === 'actif');
      setBoutiques(actives);
      const principale = actives.find(b => b.nom === 'Principale') || actives[0];
      if (principale) setBoutiqueId(principale.id);
    }).catch(() => {});
  }, []);

  const ventesFiltrees = (() => {
    const q = searchVente.trim().toLowerCase();
    const base = q
      ? ventes.filter(v =>
          String(v.montant_total).includes(q) ||
          (v.id || '').toLowerCase().includes(q) ||
          (v.lignes || []).some(l => (l.produit_nom || '').toLowerCase().includes(q)))
      : ventes;
    return base.slice(0, 30);
  })();

  const resultatsLibre = (() => {
    const q = searchLibre.trim().toLowerCase();
    if (!q) return [];
    return catalogue.filter(v =>
      (v.produit_nom || '').toLowerCase().includes(q) ||
      (v.modele || '').toLowerCase().includes(q) ||
      (q.length >= 4 && (v.code_variante || '').toLowerCase().includes(q))
    ).slice(0, 8);
  })();

  const ajouterLibre = (v) => {
    if (lignesLibres.find(l => l.variante_id === v.variante_id)) { toast('Déjà ajouté', { icon: '⚠️' }); return; }
    // Nouvel article ajouté EN TÊTE → le dernier saisi reste toujours en haut.
    setLignesLibres(prev => [{
      variante_id: v.variante_id, produit_nom: v.produit_nom, modele: v.modele,
      prix_unitaire: Number(v.prix_vente_magasin) || 0, quantite: 1,
    }, ...prev]);
    setSearchLibre('');
  };

  const total = modeRetour === 'vente'
    ? (vente?.lignes || []).reduce((s, l) => s + parseInt(retours[l.variante_id] || 0) * (parseInt(l.prix_unitaire) || 0), 0)
    : lignesLibres.reduce((s, l) => s + (parseInt(l.quantite || 0) * (parseInt(l.prix_unitaire) || 0)), 0);

  const peutValider = modeRetour === 'vente' ? !!vente : lignesLibres.length > 0;

  const submit = async () => {
    if (!motif.trim()) { toast.error('Motif obligatoire'); return; }

    // Construire les lignes selon le mode (noms alignés sur le backend)
    let lignes = [];
    let venteId = null;
    if (modeRetour === 'vente') {
      if (!vente) { toast.error('Choisis d\'abord une vente'); return; }
      venteId = vente.id;
      lignes = (vente.lignes || [])
        .filter(l => parseInt(retours[l.variante_id] || 0) > 0)
        .map(l => ({
          variante_id: l.variante_id,
          quantite_retournee: parseInt(retours[l.variante_id]),
          prix_unitaire: parseInt(l.prix_unitaire),
        }));
    } else {
      lignes = lignesLibres
        .filter(l => parseInt(l.quantite) > 0)
        .map(l => ({
          variante_id: l.variante_id,
          quantite_retournee: parseInt(l.quantite),
          prix_unitaire: parseInt(l.prix_unitaire) || 0,
        }));
    }
    if (lignes.length === 0) { toast.error('Sélectionne au moins un article'); return; }

    // Remboursement partiel : montant requis, entre 1 et le total des articles rendus
    let montantRembourse = null;
    if (type === 'remboursement_partiel') {
      montantRembourse = parseInt(montantPartiel, 10);
      if (!Number.isFinite(montantRembourse) || montantRembourse <= 0 || montantRembourse > total) {
        toast.error(`Montant à rembourser invalide (entre 1 et ${fmt(total)})`);
        return;
      }
    }

    setSaving(true);
    try {
      await api.post('/retours-clients', {
        vente_id: venteId,
        // En mode « sans reçu », on précise la boutique qui reprend la marchandise
        boutique_id: modeRetour === 'libre' ? (boutiqueId || undefined) : undefined,
        motif: motif.trim(),
        type_resolution: type,
        mode_remboursement: type.startsWith('remboursement') ? mode : null,
        montant_rembourse: montantRembourse,
        notes: notes.trim() || null,
        lignes,
      });
      toast.success('Retour enregistré');
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Échec');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="bg-gray-900 border border-gray-700 rounded-xl max-w-3xl w-full p-5 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">Nouveau retour client</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X size={18} /></button>
        </div>

        {/* Choix du mode */}
        <div className="flex gap-2 mb-4">
          {[{ k: 'vente', label: 'Depuis une vente' }, { k: 'libre', label: 'Sans reçu' }].map(m => (
            <button key={m.k} onClick={() => setModeRetour(m.k)}
              className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-colors ${
                modeRetour === m.k ? 'bg-yellow-500 text-gray-900 border-yellow-500'
                                   : 'bg-gray-800 text-gray-300 border-gray-700 hover:bg-gray-700'}`}>
              {m.label}
            </button>
          ))}
        </div>

        {/* ===== Mode : depuis une vente ===== */}
        {modeRetour === 'vente' && (
          <div className="mb-3">
            <label className="block text-xs text-gray-400 mb-1">Choisir la vente (7 derniers jours)</label>
            <div className="relative">
              <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500" />
              <input value={searchVente} onChange={e => setSearchVente(e.target.value)}
                placeholder="Filtrer par montant, produit ou code…"
                className="w-full bg-gray-800 border border-gray-700 rounded pl-7 pr-2 py-1.5 text-sm text-white" />
            </div>
            <div className="mt-2 max-h-44 overflow-y-auto border border-gray-800 rounded divide-y divide-gray-800">
              {ventesFiltrees.length === 0 ? (
                <p className="text-xs text-gray-500 p-3 text-center">Aucune vente récente</p>
              ) : ventesFiltrees.map(v => (
                <button key={v.id} onClick={() => { setVente(v); setRetours({}); }}
                  className={`w-full text-left px-3 py-2 hover:bg-gray-800 ${vente?.id === v.id ? 'bg-yellow-500/10' : ''}`}>
                  <div className="flex justify-between text-sm">
                    <span className="text-white">{fmtDate(v.date)}{v.heure ? ` · ${String(v.heure).slice(0, 5)}` : ''}</span>
                    <span className="text-yellow-400 font-bold tabular-nums">{fmt(v.montant_total)}</span>
                  </div>
                  <div className="text-xs text-gray-500 truncate">
                    {(v.lignes || []).map(l => `${l.quantite}× ${l.produit_nom}`).join(', ') || '—'}
                  </div>
                </button>
              ))}
            </div>

            {vente && (
              <div className="border border-gray-800 rounded p-2 mt-3">
                <p className="text-xs text-gray-400 mb-2">Saisis la quantité à retourner pour chaque article</p>
                <table className="w-full text-sm">
                  <thead><tr className="text-xs text-gray-400">
                    <th className="text-left p-1">Article</th>
                    <th className="text-right p-1">Vendu</th>
                    <th className="text-right p-1">Prix</th>
                    <th className="text-right p-1">À retourner</th>
                  </tr></thead>
                  <tbody>{(vente.lignes || []).map((l, i) => (
                    <tr key={l.variante_id || i} className="border-t border-gray-800">
                      <td className="p-1 text-gray-300 text-xs">{l.produit_nom} {l.modele}</td>
                      <td className="p-1 text-right text-gray-400 tabular-nums">{l.quantite}</td>
                      <td className="p-1 text-right text-gray-400 tabular-nums">{fmt(l.prix_unitaire)}</td>
                      <td className="p-1 text-right">
                        <input type="number" min="0" max={l.quantite} value={retours[l.variante_id] || ''}
                          onChange={e => setRetours({ ...retours, [l.variante_id]: e.target.value })}
                          className="w-16 bg-gray-800 border border-gray-700 rounded px-1 py-0.5 text-sm text-white text-right tabular-nums" />
                      </td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ===== Mode : sans reçu ===== */}
        {modeRetour === 'libre' && (
          <div className="mb-3">
            {boutiques.length > 1 && (
              <div className="mb-3">
                <label className="block text-xs text-gray-400 mb-1">Boutique qui reprend la marchandise *</label>
                <select value={boutiqueId} onChange={e => setBoutiqueId(e.target.value)}
                  className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-sm text-white">
                  <option value="">— Choisir —</option>
                  {boutiques.map(b => <option key={b.id} value={b.id}>{b.nom}{b.ville ? ` — ${b.ville}` : ''}</option>)}
                </select>
              </div>
            )}
            <label className="block text-xs text-gray-400 mb-1">Ajouter un article retourné</label>
            <div className="relative">
              <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500" />
              <input value={searchLibre} onChange={e => setSearchLibre(e.target.value)}
                placeholder="Rechercher un produit…"
                className="w-full bg-gray-800 border border-gray-700 rounded pl-7 pr-2 py-1.5 text-sm text-white" />
              {resultatsLibre.length > 0 && (
                <div className="absolute z-10 left-0 right-0 mt-1 bg-gray-800 border border-gray-700 rounded shadow-xl max-h-44 overflow-y-auto">
                  {resultatsLibre.map(v => (
                    <button key={v.variante_id} onClick={() => ajouterLibre(v)}
                      className="w-full flex justify-between px-3 py-2 hover:bg-gray-700 text-left text-sm">
                      <span className="text-white">{v.produit_nom} {v.modele}</span>
                      <span className="text-yellow-400">{fmt(v.prix_vente_magasin)}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {lignesLibres.length > 0 && (
              <div className="border border-gray-800 rounded p-2 mt-3 space-y-2">
                {lignesLibres.map((l, i) => (
                  <div key={l.variante_id} className="flex items-center gap-2">
                    <span className="flex-1 text-sm text-white truncate">{l.produit_nom} {l.modele}</span>
                    <input type="number" min="1" value={l.quantite} title="Quantité"
                      onChange={e => setLignesLibres(prev => prev.map((x, idx) => idx === i ? { ...x, quantite: parseInt(e.target.value) || 0 } : x))}
                      className="w-14 bg-gray-800 border border-gray-700 rounded px-1 py-0.5 text-sm text-white text-right" />
                    <InputMontant value={l.prix_unitaire} title="Prix unitaire"
                      onChange={v => setLignesLibres(prev => prev.map((x, idx) => idx === i ? { ...x, prix_unitaire: v } : x))}
                      className="w-24 bg-gray-800 border border-gray-700 rounded px-1 py-0.5 text-sm text-white text-right" />
                    <button onClick={() => setLignesLibres(prev => prev.filter((_, idx) => idx !== i))}
                      className="text-red-400 hover:text-red-300 px-1"><X size={14} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ===== Résolution + motif (commun aux deux modes) ===== */}
        {peutValider && (
          <>
            <div className="text-right text-sm mb-3">
              <span className="text-xs text-gray-400">Total retour : </span>
              <span className="text-yellow-400 font-bold tabular-nums">{fmt(total)}</span>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-xs text-gray-400 mb-1">Type de résolution</label>
                <select value={type} onChange={(e) => setType(e.target.value)}
                  className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-sm text-white">
                  <option value="remboursement_total">Remboursement total</option>
                  <option value="remboursement_partiel">Remboursement partiel</option>
                  <option value="echange">Échange</option>
                </select>
              </div>
              {type.startsWith('remboursement') && (
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Mode</label>
                  <select value={mode} onChange={(e) => setMode(e.target.value)}
                    className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-sm text-white">
                    <option value="especes">Espèces</option>
                    <option value="orange_money">Orange Money</option>
                    <option value="mtn_money">MTN Money</option>
                  </select>
                </div>
              )}
            </div>

            {type === 'remboursement_partiel' && (
              <div className="mb-3">
                <label className="block text-xs text-gray-400 mb-1">Montant à rembourser (max {fmt(total)})</label>
                <InputMontant value={montantPartiel}
                  onChange={setMontantPartiel} placeholder="0"
                  className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-sm text-white" />
              </div>
            )}

            <div className="mb-3">
              <label className="block text-xs text-gray-400 mb-1">Motif *</label>
              <input type="text" value={motif} onChange={(e) => setMotif(e.target.value)}
                placeholder="Ex: produit défectueux, taille incorrecte"
                className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-sm text-white" />
            </div>
            <div className="mb-3">
              <label className="block text-xs text-gray-400 mb-1">Notes</label>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
                className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-sm text-white" />
            </div>
          </>
        )}

        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-300 hover:bg-gray-800 rounded-lg">Annuler</button>
          <button onClick={submit} disabled={saving || !peutValider}
            className="px-4 py-2 bg-yellow-500 hover:bg-yellow-600 disabled:opacity-40 text-gray-900 text-sm font-medium rounded-lg flex items-center gap-2">
            {saving ? <Loader size={14} className="animate-spin" /> : <RotateCcw size={14} />} Enregistrer
          </button>
        </div>
      </div>
    </div>
  );
}

function ModalDetail({ id, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/retours-clients/${id}`).then(r => setData(r.data)); }, [id]);
  if (!data) return null;
  // Le backend renvoie les champs du retour au niveau racine + un tableau `lignes`.
  const { lignes = [], ...retour } = data;
  const tr = TYPES_RES[retour.type_resolution] || { label: retour.type_resolution, color: '' };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        className="bg-gray-900 border border-gray-700 rounded-xl max-w-2xl w-full p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-white">Retour #{retour.numero || retour.id?.substring(0, 8)}</h3>
          <span className={`px-2 py-1 rounded border text-xs ${tr.color}`}>{tr.label}</span>
        </div>
        <p className="text-sm text-gray-400 mb-2"><span className="text-gray-500">Motif :</span> {retour.motif}</p>
        <p className="text-sm text-gray-400 mb-2"><span className="text-gray-500">Date :</span> {fmtDate(retour.created_at)}</p>
        {retour.montant_rembourse > 0 && (
          <p className="text-sm text-emerald-400 mb-3">Remboursé : {fmt(retour.montant_rembourse)}</p>
        )}
        <table className="w-full text-sm border border-gray-800 rounded">
          <thead><tr className="text-xs text-gray-400 bg-gray-800/50">
            <th className="text-left p-1">Article</th><th className="p-1 text-right">Qté</th><th className="p-1 text-right">Total</th>
          </tr></thead>
          <tbody>{lignes.map((l, i) => (
            <tr key={i} className="border-t border-gray-800">
              <td className="p-1 text-gray-300 text-xs">{l.produit_nom || l.code_variante}</td>
              <td className="p-1 text-right text-gray-400 tabular-nums">{l.quantite}</td>
              <td className="p-1 text-right text-white tabular-nums">{fmt(l.quantite * l.prix_unitaire)}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    </div>
  );
}

export default function PageRetoursClients() {
  const { canEditBusiness } = useAuth();
  const [retours, setRetours] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreer, setShowCreer] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [search, setSearch] = useState('');

  const charger = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/retours-clients');
      setRetours(data.retours || data || []);
    } catch (err) {
      toast.error('Échec chargement');
    } finally { setLoading(false); }
  };
  useEffect(() => { charger(); }, []);

  const filtres = retours.filter(r => !search ||
    r.numero?.toLowerCase().includes(search.toLowerCase()) ||
    r.motif?.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="p-4 sm:p-6 space-y-4">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-yellow-500/15 border border-yellow-500/30 rounded-lg flex items-center justify-center">
            <RotateCcw size={18} className="text-yellow-400" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white">Retours clients</h1>
            <p className="text-xs text-gray-500">Remboursements, avoirs et échanges</p>
          </div>
        </div>
        {canEditBusiness && (
          <button onClick={() => setShowCreer(true)}
            className="flex items-center gap-2 px-3 py-2 bg-yellow-500 hover:bg-yellow-600 text-gray-900 text-sm font-medium rounded-lg">
            <Plus size={14} /> Nouveau retour
          </button>
        )}
      </header>

      <div className="bg-gray-900 border border-gray-800 rounded-lg p-3">
        <div className="relative">
          <Search size={12} className="absolute left-2 top-2.5 text-gray-500" />
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="N° ou motif"
            className="w-full max-w-xs bg-gray-800 border border-gray-700 rounded pl-7 pr-2 py-1.5 text-sm text-white" />
        </div>
      </div>

      <div className="bg-gray-900 border border-gray-800 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-800/50">
            <tr className="text-left text-xs text-gray-400">
              <th className="px-3 py-2">N°</th>
              <th className="px-3 py-2">Date</th>
              <th className="px-3 py-2">Motif</th>
              <th className="px-3 py-2">Résolution</th>
              <th className="px-3 py-2 text-right">Montant</th>
              <th className="px-3 py-2 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="text-center text-gray-500 py-8">
                <Loader size={20} className="animate-spin mx-auto" />
              </td></tr>
            ) : filtres.length === 0 ? (
              <tr><td colSpan={6} className="text-center text-gray-500 py-8">Aucun retour</td></tr>
            ) : filtres.map(r => {
              const tr = TYPES_RES[r.type_resolution] || { label: r.type_resolution, color: '' };
              return (
                <tr key={r.id} className="border-t border-gray-800 hover:bg-gray-800/30">
                  <td className="px-3 py-2 font-mono text-xs text-gray-300">{r.numero || r.id?.substring(0, 8)}</td>
                  <td className="px-3 py-2 text-gray-400 text-xs">{fmtDate(r.created_at)}</td>
                  <td className="px-3 py-2 text-gray-300 max-w-xs truncate">{r.motif}</td>
                  <td className="px-3 py-2">
                    <span className={`px-2 py-0.5 rounded text-xs border ${tr.color}`}>{tr.label}</span>
                  </td>
                  <td className="px-3 py-2 text-right text-emerald-400 tabular-nums">
                    {r.montant_rembourse ? fmt(r.montant_rembourse) : '—'}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => setSelectedId(r.id)} className="text-gray-400 hover:text-white"><Eye size={14} /></button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showCreer && <ModalCreer onClose={() => setShowCreer(false)} onSuccess={() => { setShowCreer(false); charger(); }} />}
      {selectedId && <ModalDetail id={selectedId} onClose={() => setSelectedId(null)} />}
    </div>
  );
}
